import { memo, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { truncate } from "lodash";

import Button from "@mui/material/Button";
import Check from "@zenith/assets/icons/Check";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import {
  AutocompleteComponent,
  InitialsSquareComponent,
  ModalComponent,
  SpinnerComponent,
  TextFieldComponent,
  ToastSnackbarComponent,
  WalletComponent,
} from "@zenith/components";
import {
  ApiResponseDTOAccountLimitsStatusDTO,
  ApiResponseDTOExceededLimitsDTO,
  BeneficiaryApiService,
  BeneficiaryDetailsRecord,
  LimitsApiService,
} from "@zenith/services/user";
import { handleBeneficiary } from "@zenith/utils/Helpers";
import {
  useNotificationContext,
  useTransactionsContext,
} from "@zenith/context";
import {
  DASHBOARD,
  TRANSACTIONS,
  TRANSACTIONS_REFUND_OVERVIEW,
  TRANSACTIONS_TRANSACTION_DETAILS,
  UPGRADE_ACCOUNT,
} from "@zenith/routes/paths";
import {
  CONNECTION_ERROR_MESSAGE,
  DISCARD_REFUND_BUTTON,
  LIMIT_EXCEEDED_ERROR_MODAL_BODY,
  LIMIT_EXCEEDED_ERROR_MODAL_SECOND_LINE,
  LIMIT_EXCEEDED_ERROR_MODAL_TITLE,
  MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH,
  NO_BENEFICIARY_WAS_FOUND_ERROR_CODE,
  NUMBER_INPUT_REGEX,
  ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
  CURRENT_ZENITH_NIGERIA_BANK_ID,
  POST_NO_DEBIT,
  POST_NO_DEBIT_MESSAGE,
  UPGRADE_ACCOUNT_BUTTON,
  WALLET,
} from "@zenith/data/constants";
import styles from "./refund-page.module.scss";
import { useSendMoneyContext } from "@zenith/context";
import { AppNavigation } from "@zenith/hooks";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import UserNotFound from "@zenith/assets/icons/components/UserNotFound";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { UserSquare } from "@zenith/assets/icons/components";
import IconButton from "@mui/joy/IconButton/IconButton";
import { TransactionsContextType } from "@zenith/context/global-context/types";
import { FormFieldErrorType } from "@zenith/types";
import { sortedBeneficiaryDetailsRecord } from "@zenith/pages/BeneficiariesPage/beneficiaries-page/beneficiaries-page";
import { AutocompleteSelectItemsType } from "@zenith/components/ui/AutocompleteComponent/autocomplete-component/interfaces";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import {
  ApiResponseDTO,
  ApiResponseDTOListBankRecord,
  BankApiService,
  BankRecord,
  BeneficiaryRecord,
  WalletApiService as TransactionWalletApiService,
} from "@zenith/services/transactions";
import { getErrorTitle } from "@zenith/utils/Helpers/getAPIErrorTitle";
import { ERROR_MESSAGES } from "@zenith/pages/SendMoneyPage/constants";
import { RestrictedAccountComponent } from "@zenith/features/Payments/components";
import { CheckBox } from "@zenith/components/ui/CheckBoxComponent/checkbox-component";

interface FormDataType {
  amount: number;
  refundReason?: string;
}

interface ResolvedBeneficiary {
  success: boolean;
  beneficiaryName?: string;
  nameEnquiryRef?: string;
  message?: string;
}

async function resolveBeneficiary(
  accountNumber: string,
  bankId: string
): Promise<ResolvedBeneficiary> {
  if (bankId === CURRENT_ZENITH_NIGERIA_BANK_ID) {
    const response = await TransactionWalletApiService.getWalletHolderInfo(
      accountNumber,
      bankId
    );
    return {
      success: !!response.success,
      beneficiaryName: response.data?.accountOwner,
      nameEnquiryRef: response.data?.nameEnquiryRef,
      message: response.message,
    };
  }

  const response = await BeneficiaryApiService.getBankBeneficiary(
    accountNumber,
    bankId
  );
  return {
    success: !!response.success,
    beneficiaryName: response.data?.beneficiaryName,
    nameEnquiryRef: response.data?.nameEnquiryRef,
    message: response.message,
  };
}

interface FormErrors {
  beneficiaryAccountNumber: FormFieldErrorType;
  bankName: FormFieldErrorType;
  beneficiaryName: FormFieldErrorType;
  amount: FormFieldErrorType;
  refundReason?: string;
}

const RefundPage = memo(function RefundPage() {
  const navigate = useNavigate();
  const { sendMoneyContext, setSendMoneyContext, clearSendMoneyContext } =
    useSendMoneyContext();

  const {
    transactionsContext,
    setTransactionsContext,
    clearTransactionsContext,
  } = useTransactionsContext();

  const {
    amount,
    bankId,
    bankName,
    beneficiaryName,
    description,
    paymentMethod,
  } = transactionsContext;

  const initialState: TransactionsContextType = {
      accountOwner: "",
      amount: Number(""),
      bankName: "",
      bankId: "",
      beneficiaryAccountNumber: "",
      beneficiaryName: "",
      description: "",
    },
    initialFormErrorState: FormErrors = {
      beneficiaryAccountNumber: {
        message: "",
        hasError: false,
      },
      bankName: {
        message: "",
        hasError: false,
      },
      beneficiaryName: {
        message: "",
        hasError: false,
      },
      amount: {
        message: "",
        hasError: false,
      },
    };

  const { navigateTo } = AppNavigation();

  const [bankList, setBankList] = useState<
    AutocompleteSelectItemsType[] | undefined
  >([]);
  const [availableLimit, setAvailableLimit] = useState<number>(0);
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [saveBeneficiary, setSaveBeneficiary] = useState<boolean>(false);
  const [isBeneficiarySaved, setIsBeneficiarySaved] = useState(saveBeneficiary);
  const [isLoadingWalletDetails, setIsLoadingWalletDetails] =
    useState<boolean>(false);

  useEffect(() => {
    setIsBeneficiarySaved(saveBeneficiary);
  }, [saveBeneficiary]);

  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [isInsufficientFunds, setIsInsufficientFunds] = useState(false);
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [showBeneficiaryList, setShowBeneficiaryList] =
    useState<boolean>(false);
  const [nameEnquiryRef, setNameEnquiryRef] = useState<string | undefined>(
    undefined
  );
  const [isResolvingBeneficiaryName, setIsResolvingBeneficiaryName] =
    useState<boolean>(false);
  const [beneficiaryList, setBeneficiaryList] =
    useState<sortedBeneficiaryDetailsRecord[]>();
  const [beneficiariesError, setBeneficiariesError] = useState<boolean>(false);
  const [isLoadingBeneficiaries, setIsLoadingBeneficiaries] =
    useState<boolean>(false);
  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<sortedBeneficiaryDetailsRecord>();
  const [savingBeneficary, setSavingBeneficary] = useState<boolean>(false);

  const [formErrors, setFormErrors] = useState<FormErrors>(
    initialFormErrorState
  );

  const [formData, setFormData] =
    useState<TransactionsContextType>(initialState);

  const [showPostNoDebitModal, setShowPostNoDebitModal] =
    useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");

  const { setOpen } = useNotificationContext();
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
  }

  useEffect(() => {
    const beneficiaryAccountNumber = formData.beneficiaryAccountNumber;

    if (
      !formData.amount &&
      amount &&
      !formData.bankName &&
      bankName &&
      !formData.bankId &&
      bankId
    ) {
      setFormData({
        accountOwner: "",
        amount: amount || Number(""),
        bankName: bankName || "",
        bankId: bankId || "",
        beneficiaryAccountNumber: beneficiaryAccountNumber || "",
        beneficiaryName: beneficiaryName || beneficiaryName === "N/A" ? "" : "",
        description: description || "",
      });
    }

    if (
      beneficiaryAccountNumber &&
      formData.beneficiaryAccountNumber.length ===
        MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH &&
      formData.bankId &&
      !selectedBeneficiary &&
      !formData.beneficiaryName
    ) {
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          message: "",
          hasError: false,
        },
      });

      setIsResolvingBeneficiaryName(true);

      const showBeneficiaryNotFound = () => {
        setFormData({
          ...formData,
          beneficiaryName: "",
        });
        setFormErrors({
          ...formErrors,
          beneficiaryName: {
            message:
              "No account name was found. Please verify account number and/or bank name.",
            hasError: true,
          },
        });
      };

      const showAccountNumberError = (message: string) => {
        setFormData({
          ...formData,
          beneficiaryName: "",
        });
        setFormErrors({
          ...formErrors,
          beneficiaryAccountNumber: {
            message,
            hasError: true,
          },
          beneficiaryName: {
            message: "",
            hasError: false,
          },
        });
      };

      resolveBeneficiary(beneficiaryAccountNumber, formData.bankId)
        .then((response) => {
          if (response.success && response.beneficiaryName) {
            handleBeneficiaryNameChange(response.beneficiaryName);
            setNameEnquiryRef(response.nameEnquiryRef);
            setFormErrors({
              ...formErrors,
              beneficiaryAccountNumber: {
                message: "",
                hasError: false,
              },
              beneficiaryName: {
                message: "",
                hasError: false,
              },
            });
            return;
          }

          if (response.message) {
            showAccountNumberError(response.message);
          } else {
            showBeneficiaryNotFound();
          }
        })
        .catch((error) => {
          if (error.body?.internalCode === NO_BENEFICIARY_WAS_FOUND_ERROR_CODE) {
            showBeneficiaryNotFound();
          } else if (error.body?.message) {
            showAccountNumberError(error.body.message);
          }

          throw new Error(error);
        })
        .finally(() => {
          setIsResolvingBeneficiaryName(false);
        });
    }

    if (!isLoadingWalletDetails && amount > availableBalance) {
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        amount: {
          message: ERROR_MESSAGES.INSUFFICIENT_BALANCE,
          hasError: true,
        },
      }));
    } else {
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        amount: {
          message: "",
          hasError: false,
        },
      }));
    }
  }, [formData.beneficiaryAccountNumber, formData.bankId, formData.beneficiaryName, amount, availableBalance, isLoadingWalletDetails]);

  useEffect(() => {
    if (!bankList?.length) {
      BankApiService.getBanks()
        .then((response: ApiResponseDTOListBankRecord) => {
          if ("data" in response && response.success) {
            const banks: SelectItemsType[] | undefined = response.data?.map(
              (bank: BankRecord) => ({
                value: bank.id.toString(),
                label: bank.bankName,
              })
            );

            setBankList(banks);
          }
        })
        .catch((error) => {
          throw new Error(error);
        });
    }
  }, []);

  useEffect(() => {
    const {
      amount,
      bankName,
      bankId,
      bankCode,
      nameEnquiryRef,
      beneficiaryAccountNumber,
      beneficiaryName,
      description,
    } = transactionsContext;

    if (amount) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        amount,
        bankName: bankName || "",
        bankId: bankId || "",
        bankCode: bankCode || "",
        nameEnquiryRef: nameEnquiryRef || "",
        beneficiaryAccountNumber: beneficiaryAccountNumber || "",
        beneficiaryName: beneficiaryName || beneficiaryName === "N/A" ? "" : "",
        description: description || "",
      }));
    }
  }, [amount]);

  useEffect(() => {
    if (!availableLimit) {
      setIsLoadingWalletDetails(true);
      LimitsApiService.getAccountStatus()
        .then((response: ApiResponseDTOAccountLimitsStatusDTO) => {
          setAvailableLimit(Number(response.data?.debit?.availableLimit));
          setAvailableBalance(Number(response.data?.balance?.availableBalance));
          setAccountNumber(response.data?.accountNumber as string);
        })
        .catch((error) => {
          throw new Error(error);
        })
        .finally(() => {
          setIsLoadingWalletDetails(false);
        });
    }
  }, [availableLimit]);

  useEffect(() => {
    setIsLoadingBeneficiaries(true);
    BeneficiaryApiService.getBeneficiary()
      .then((response: BeneficiaryRecord[] | ApiResponseDTO) => {
        if ("data" in response && response.data) {
          setBeneficiaryList(
            response.data?.beneficiaryDetails.map(
              (beneficiary: BeneficiaryDetailsRecord) =>
                handleBeneficiary(beneficiary)
            )
          );
          setIsLoadingBeneficiaries(false);
        }
      })
      .catch((error) => {
        if (error.body.internalCode === NO_BENEFICIARY_WAS_FOUND_ERROR_CODE) {
          setBeneficiaryList([]);
        } else {
          setBeneficiariesError(true);
        }
        setIsLoadingBeneficiaries(false);
        throw new Error(error);
      });
  }, []);

  useEffect(() => {
    LimitsApiService.getExceededLimits().then(
      (response: ApiResponseDTOExceededLimitsDTO) => {
        const hasLimits = Boolean(
          response.data?.creditLimitExceeded ||
            response.data?.balanceLimitExceeded
        );

        if (
          "data" in response &&
          response.data &&
          hasLimits &&
          response.data.needsUpgradeToTier
        ) {
          setShowPostNoDebitModal(hasLimits);
          setModalMessage(
            POST_NO_DEBIT_MESSAGE.mapUpgradeTier(
              response.data.needsUpgradeToTier
            )
          );
        }
      }
    );
  }, []);

  if (!amount) {
    //fix
    clearTransactionsContext();
    return <Navigate to={TRANSACTIONS} replace />;
  }

  function handleBackButton(): void {
    navigate(TRANSACTIONS_TRANSACTION_DETAILS, {
      replace: true,
    });
  }

  function handleAccountNumberBlur(
    event: React.FocusEvent<HTMLInputElement>
  ): void {
    if (!formData.beneficiaryAccountNumber.length && !showBeneficiaryList) {
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          message: "Account number is required",
          hasError: true,
        },
      });
      if (formData.beneficiaryName) {
        setFormData({
          ...formData,
          beneficiaryName: "",
        });
      }
      return;
    }

    if (
      formData.beneficiaryAccountNumber.length <
        MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH &&
      !showBeneficiaryList
    ) {
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          message: "Beneficiary account number not valid",
          hasError: true,
        },
      });

      if (formData.beneficiaryName) {
        setFormData({
          ...formData,
          beneficiaryName: "",
        });
      }
      return;
    }

    setSelectedBeneficiary(undefined);
  }

  function handleSelectedBeneficiary(
    beneficiary: sortedBeneficiaryDetailsRecord
  ) {
    setSelectedBeneficiary(beneficiary);
    const { bankName, accountNumber, bankId, beneficiaryName } = beneficiary;
    if (bankName && accountNumber && bankId && beneficiaryName) {
      setFormData({
        ...formData,
        bankName,
        beneficiaryName,
        bankId: String(bankId),
        beneficiaryAccountNumber: accountNumber,
      });
    }
    setIsBeneficiarySaved(true);
    setShowBeneficiaryList(false);
  }

  function handleShowBeneficiaryList() {
    setFormErrors({
      ...formErrors,
      beneficiaryAccountNumber: {
        message: "",
        hasError: false,
      },
    });
    if (!formData.beneficiaryAccountNumber.length && showBeneficiaryList) {
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          message: "Beneficiary account number is required",
          hasError: true,
        },
      });
    }
    if (
      formData.beneficiaryAccountNumber.length <
        MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH &&
      showBeneficiaryList
    ) {
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          message: "Beneficiary account number not valid",
          hasError: true,
        },
      });
    }
    if (
      (!formData.beneficiaryAccountNumber.length ||
        formData.beneficiaryAccountNumber.length <
          MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH) &&
      showBeneficiaryList &&
      formData.beneficiaryName
    ) {
      setFormData({
        ...formData,
        beneficiaryName: "",
      });
    }

    setShowBeneficiaryList(!showBeneficiaryList);
  }

  function handleBeneficiaryNameChange(beneficiaryName: string): void {
    setFormData({
      ...formData,
      beneficiaryName,
    });
  }

  function handleBankTextChange() {
    setSelectedBeneficiary(undefined);
  }

  const checkSaveBeneficiaryFields = () =>
    [
      formData.beneficiaryAccountNumber,
      !formErrors.beneficiaryAccountNumber.hasError,
      formData.bankId,
      !formErrors.bankName.hasError,
      formData.beneficiaryName,
      !formErrors.beneficiaryName.hasError,
    ].every((value) => !!value);

  const handleSaveBeneficiaryChecked = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSaveBeneficiary(event.target.checked);
  };

  const handleSaveBeneficiaryChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsBeneficiarySaved(event.target.checked);
    setSaveBeneficiary(event.target.checked);
  };

  function handleBankChange(value: string, label: string): void {
    if (value !== formData.bankId || label !== formData.bankName) {
      setFormData({
        ...formData,
        bankId: value,
        bankName: label,
        beneficiaryName: "",
      });
      setSelectedBeneficiary(undefined);
    }
  }

  async function handleSubmit(): Promise<void> {
    if (amount > availableLimit) {
      setIsLimitExceeded(true);
      return;
    }

    const response = await resolveBeneficiary(
      formData.beneficiaryAccountNumber,
      formData.bankId
    );

    if (!response.success) {
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          hasError: true,
          message:
            response.message || "Unable to validate account. Please try again.",
        },
      });
      return;
    }

    if (response.nameEnquiryRef) {
      setNameEnquiryRef(response.nameEnquiryRef);
    } else if (formData.bankId !== CURRENT_ZENITH_NIGERIA_BANK_ID) {
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          hasError: true,
          message: "Unable to validate account. Please try again.",
        },
      });
      return;
    }

    if (saveBeneficiary) {
      const request = {
        accountNumber: formData.beneficiaryAccountNumber,
        bankId: formData.bankId,
        bankName: formData.bankName,
        beneficiaryName: formData.beneficiaryName,
      };
      setSavingBeneficary(true);

      BeneficiaryApiService.postBeneficiary(request)
        .then(() => {
          setOpen(true);
          notify(
            "Beneficiary Added",
            "The beneficiary has been successfully added to your list.",
            "success"
          );
        })
        .catch((error) => {
          const apiErrorMessage =
            error?.body?.message || CONNECTION_ERROR_MESSAGE;
          const title = getErrorTitle(error?.status);
          const message = apiErrorMessage;

          setOpen(true);
          notify(title, message, "error");
        })
        .finally(() => {
          setSavingBeneficary(false);
          setTransactionsContext({
            ...transactionsContext,
            nameEnquiryRef,
            bankCode: formData.bankId,
            beneficiaryAccountNumber: formData.beneficiaryAccountNumber,
            beneficiaryName: formData.beneficiaryName,
            bankId: formData.bankId,
            bankName: formData.bankName,
            refundReason: formData.refundReason || "",
          });
          setTimeout(() => {
            setOpen(false);
            navigate(TRANSACTIONS_REFUND_OVERVIEW, {
              replace: true,
              state: {
                nameEnquiry: nameEnquiryRef || response.nameEnquiryRef,
              },
            });
          }, 1000);
        });
    } else {
      setTransactionsContext({
        ...transactionsContext,
        nameEnquiryRef,
        bankCode: formData.bankId,
        beneficiaryAccountNumber: formData.beneficiaryAccountNumber,
        beneficiaryName: formData.beneficiaryName,
        bankId: formData.bankId,
        bankName: formData.bankName,
        refundReason: formData.refundReason || "",
      });

      navigate(TRANSACTIONS_REFUND_OVERVIEW, {
        replace: true,
        state: { nameEnquiry: nameEnquiryRef || response.nameEnquiryRef },
      });
    }
  }

  function handleAccountNumberChange(
    event: React.FocusEvent<HTMLInputElement>
  ): void {
    const newAccountNumber = event.target.value.replace(NUMBER_INPUT_REGEX, "");

    if (newAccountNumber !== formData.beneficiaryAccountNumber) {
      setFormData({
        ...formData,
        beneficiaryAccountNumber: newAccountNumber,
        beneficiaryName: "",
      });
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        beneficiaryAccountNumber: {
          message: "",
          hasError: false,
        },
      }));
      setSelectedBeneficiary(undefined);
    }
  }

  function handleRefundReasonChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    // ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX = new RegExp(/[^a-zA-Z0-9 ]/gi);
    setFormData({
      ...formData,
      refundReason: event.target.value.replace(
        ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
        ""
      ) as string,
    });
  }

  function isContinueButtonDisabled(): boolean {
    return (
      !formData.amount ||
      !formData.beneficiaryAccountNumber ||
      !formData.beneficiaryName ||
      formData.amount > availableBalance
    );
  }

  function handleModalClose() {
    setIsInsufficientFunds(false);
    setIsLimitExceeded(false);
  }

  function handleDiscardRefund() {
    setIsLimitExceeded(false);
    setIsInsufficientFunds(false);
    clearTransactionsContext();
    navigate(DASHBOARD);
  }

  function handleUpgradeTier() {
    setIsLimitExceeded(false);
    setIsInsufficientFunds(false);
    clearTransactionsContext();
    navigate(UPGRADE_ACCOUNT);
  }

  function handleUpgradeAccountTier(): void {
    navigateTo(UPGRADE_ACCOUNT);
  }

  function handleNavigateToDashboard(): void {
    navigateTo(DASHBOARD);
  }

  const showValidatingIndicator =
    isResolvingBeneficiaryName &&
    formData.beneficiaryAccountNumber.length ===
      MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH &&
    !formData.beneficiaryName;

  return (
    <>
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />

      <RestrictedAccountComponent type="send-money" />

      <ModalComponent
        title={LIMIT_EXCEEDED_ERROR_MODAL_TITLE}
        body={`${LIMIT_EXCEEDED_ERROR_MODAL_BODY} ${LIMIT_EXCEEDED_ERROR_MODAL_SECOND_LINE}`}
        showModal={isLimitExceeded}
        onClickClose={handleModalClose}
        onClickButton={handleModalClose}
        doubleOptions
        leftButtonText={DISCARD_REFUND_BUTTON}
        rightButtonText={UPGRADE_ACCOUNT_BUTTON}
        onLeftButtonClick={handleDiscardRefund}
        onRightButtonClick={handleUpgradeTier}
      />

      <ModalComponent
        title={"Insufficient Funds"}
        body={"Insufficient funds to transfer the intended amount."}
        showModal={isInsufficientFunds}
        buttonText={DISCARD_REFUND_BUTTON}
        onClickClose={handleModalClose}
        onClickButton={handleDiscardRefund}
      />
      <div className="mb-3 flex-col items-start justify-between">
        <Button
          id="back-cart-summary"
          onClick={() => handleBackButton()}
          className="text-base font-normal normal-case text-neutral-600 hover:bg-white"
        >
          <ArrowBackIosIcon fontSize="small" /> Back
        </Button>
        <div className="m-auto mt-5 w-full rounded-[20px] border border-neutral-200 lg:w-3/5">
          <div className="rounded-2.5xl bg-white py-6 px-8">
            <div className=" flex-col justify-between">
              <h1 className="mb-8 text-2xl font-medium text-neutral-900">
                Refund
              </h1>
            </div>

            <WalletComponent
              accountNumber={accountNumber}
              availableBalance={availableBalance}
              availableLimit={availableLimit}
              isInfoLoaded={() => true}
              balanceTitle="Acc Balance"
              limitTitle="Transaction Limit"
            />
            <h2 className="mt-6 mb-4 text-xl font-medium text-neutral-900">
              Account to Refund
            </h2>
            <div className="mb-8 flex flex-col gap-4">
              {selectedBeneficiary ? (
                <TextFieldComponent
                  id="refund-bank-name-text-field"
                  label="Bank Name"
                  labelId="bank-name-textfield-label"
                  className="h-12 disabled:text-neutral-600"
                  placeholder="Select bank name"
                  type="text"
                  value={formData.bankName || "Bank name"}
                  disabled
                  maxLength={10}
                  onChange={() => handleBankTextChange()}
                />
              ) : (
                <AutocompleteComponent
                  id="refund-bank-name"
                  label="Bank Name"
                  labelId="bank-name-dropdown-label"
                  className="w-full"
                  placeholder="Select bank name"
                  onChange={handleBankChange}
                  options={bankList || []}
                  selectValue={
                    formData.bankName && formData.bankId
                      ? { label: formData.bankName, value: formData.bankId }
                      : undefined
                  }
                  showSearchIcon
                />
              )}

              <div className="relative">
                <TextFieldComponent
                  id="account-number-field"
                  label="Account Number"
                  labelId="account-number-field-label"
                  placeholder="Enter an account number"
                  type="text"
                  className={`border-neutral-300 pr-4 pl-4 shadow-none before:shadow-neutral-900 ${
                    showBeneficiaryList
                      ? "border-b-0 before:shadow-none [&.fieldset]:border-0 [&.text-field]:rounded-b-none"
                      : ""
                  }`}
                  value={formData.beneficiaryAccountNumber}
                  maxLength={10}
                  onChange={handleAccountNumberChange}
                  onBlur={handleAccountNumberBlur}
                  endDecorator={
                    <IconButton
                      color="neutral"
                      onClick={handleShowBeneficiaryList}
                    >
                      <UserSquare width={24} height={24} />
                    </IconButton>
                  }
                />
                {showBeneficiaryList && (
                  <div
                    className={`bottom-100 absolute z-[1300] ${
                      !beneficiaryList ||
                      beneficiaryList.length === 0 ||
                      beneficiariesError
                        ? "h-44"
                        : "h-230"
                    } w-full rounded-b-xl border border-t-0 border-neutral-300 bg-white p-3 pt-0`}
                  >
                    <hr />
                    <div className="mt-2 h-full overflow-y-auto pb-1">
                      {isLoadingBeneficiaries && (
                        <div className="flex h-full w-full items-center justify-center">
                          <CircularProgress
                            className={"text-primary-300"}
                            size={"4rem"}
                          />
                        </div>
                      )}
                      {beneficiariesError && !isLoadingBeneficiaries && (
                        <div className="flex h-full flex-col items-center justify-center">
                          <CloseCircle width={24} height={24} fill="#DA291C" />
                          <p className="mt-2 text-center font-medium text-neutral-900">
                            Something Went Wrong
                          </p>
                          <p className="w-56 text-center text-sm text-neutral-600">
                            We could not load beneficiaries list. Please try
                            again.
                          </p>
                        </div>
                      )}

                      {beneficiariesError && !isLoadingBeneficiaries && (
                        <div className="flex h-full flex-col items-center justify-center">
                          <CloseCircle width={24} height={24} fill="#DA291C" />
                          <p className="mt-2 text-center font-medium text-neutral-900">
                            Something Went Wrong
                          </p>
                          <p className="w-56 text-center text-sm text-neutral-600">
                            We could not load beneficiaries list. Please try
                            again.
                          </p>
                        </div>
                      )}
                      {!beneficiaryList ||
                        (beneficiaryList?.length === 0 && (
                          <div className="flex h-full flex-col items-center justify-center">
                            <div className="bg-rose-150 flex h-9 w-9 items-center justify-center rounded-full">
                              <UserNotFound />
                            </div>
                            <p className="mt-2 text-center font-medium text-neutral-900">
                              No Beneficiaries Found
                            </p>
                            <p className="w-60 text-center text-sm text-neutral-600">
                              It seems there are no beneficiaries to display at
                              the moment.
                            </p>
                          </div>
                        ))}
                      {!isLoadingBeneficiaries &&
                        !beneficiariesError &&
                        beneficiaryList?.map((beneficiary, idx) => (
                          <div
                            className="mb-0.5 flex cursor-pointer items-center rounded-lg py-3 pl-1 pr-3 hover:bg-neutral-25"
                            key={idx}
                            onClick={() =>
                              handleSelectedBeneficiary(beneficiary)
                            }
                          >
                            <InitialsSquareComponent
                              displayName={beneficiary.displayName}
                              size="sm"
                            />
                            <div className="flex items-center">
                              <div>
                                <p className="text-neutral-600">
                                  {truncate(beneficiary.displayName, {
                                    length: 25,
                                  })}
                                </p>
                                <p className="text-sm text-neutral-600">
                                  {beneficiary.accountNumber} -{" "}
                                  {beneficiary.bankName}{" "}
                                </p>
                              </div>
                              <span className="ml-4">
                                {selectedBeneficiary?.accountNumber ===
                                  beneficiary.accountNumber && <Check />}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                {formErrors.beneficiaryAccountNumber.hasError && (
                  <div className="mx-1.5 mt-1 w-full text-sm text-red-400">
                    <span className="break-words">
                      {formErrors.beneficiaryAccountNumber.message}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <TextFieldComponent
                  id="Account-name-field"
                  label="Account Name"
                  labelId="beneficiary-name-field-label"
                  className="h-12 disabled:text-neutral-600"
                  placeholder="Enter an account number or select a beneficiary"
                  type="text"
                  value={formData.beneficiaryName || "Beneficiary name"}
                  disabled
                  disabledTextColor={
                    formData.beneficiaryName ? "#626262" : undefined
                  }
                  maxLength={10}
                  onChange={(a) => handleBeneficiaryNameChange(a.target.value)}
                  endDecorator={
                    showValidatingIndicator ? (
                      <SpinnerComponent
                        size={20}
                        type="primary"
                        showIcon={false}
                      />
                    ) : undefined
                  }
                />
                {formErrors.beneficiaryName.hasError && (
                  <div className="mx-1.5 mt-1 w-full text-sm text-red-400">
                    <span className="break-words">
                      {formErrors.beneficiaryName.message}
                    </span>
                  </div>
                )}
              </div>
              {checkSaveBeneficiaryFields() && (
                <FormGroup className="w-max">
                  <CheckBox
                    label="Save Beneficiary"
                    state={isBeneficiarySaved ? "checked" : "unchecked"}
                    onChange={(nextState) => {
                      const checked = nextState === "checked";
                      setIsBeneficiarySaved(checked);
                      handleSaveBeneficiaryChange({
                        target: { checked },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                  />
                </FormGroup>
              )}
            </div>

            <div className="mb-10 flex flex-col gap-2">
              <div className="text-sm font-normal leading-none text-neutral-400">
                Transaction type
              </div>
              <div className="text-base font-normal leading-normal text-zinc-600">
                {paymentMethod?.mapPaymentMethod()}
              </div>
            </div>

            <h2 className="mt-6 mb-4 text-xl font-medium text-neutral-900">
              Refund Amount
            </h2>

            <div className="mb-8 flex flex-col gap-4">
              <div>
                <div className="ml-2 mb-1 text-sm text-neutral-500">Amount</div>

                <div className={"w-full " + styles.currencyWrapper}>
                  <NumericFormat
                    allowNegative={false}
                    placeholder="0.00"
                    value={formData.amount || ""}
                    id="transfer-details-amount-input"
                    className="h-[48px] w-full rounded-1.5lg border px-4 py-3 pl-11 text-lg leading-normal text-black focus:outline-neutral-900"
                    decimalScale={2}
                    fixedDecimalScale
                    allowLeadingZeros={false}
                    thousandSeparator={true}
                    disabled
                    isAllowed={(values: { value: string }) => {
                      const totalCharacters = values.value
                        .split("")
                        .filter((char: string) => char !== " ").length;
                      return totalCharacters <= 16;
                    }}
                  />
                </div>
                {formErrors.amount.hasError && (
                  <div className="mx-1.5 mt-1 text-sm text-red-400">
                    {formErrors.amount.message}
                  </div>
                )}
              </div>

              <div className="w-full">
                <TextFieldComponent
                  type="text"
                  id="send-money-textarea"
                  labelId="send-money-textarea-label"
                  label="Refund Reason (Optional)"
                  placeholder="Refund reason"
                  value={formData.refundReason as string}
                  onChange={(e) => handleRefundReasonChange(e)}
                  maxLength={50}
                />
              </div>
            </div>

            <p className="text-sm text-[#626262]">
              Disclaimer: All refunds are carried out via instant bank transfer
              and applicable charges for the service will be applied to the
              refund operation.
            </p>

            <div className="mt-12 flex justify-center">
              <Button
                id="checkout-cart-button"
                className={
                  "h-12 w-[212px] gap-2 self-center rounded-lg bg-primary-300 py-3 px-10 text-base font-medium normal-case text-white hover:bg-primary-300 disabled:bg-zinc-100"
                }
                size="large"
                onClick={handleSubmit}
                disabled={isContinueButtonDisabled() || savingBeneficary}
              >
                Continue{" "}
                {savingBeneficary ? (
                  <CircularProgress className="text-zinc-600" size={"16px"} />
                ) : (
                  ""
                )}
              </Button>
            </div>
          </div>
        </div>
        <ModalComponent
          title={POST_NO_DEBIT}
          body={modalMessage}
          showModal={showPostNoDebitModal}
          onClickButton={handleNavigateToDashboard}
          onClickClose={handleNavigateToDashboard}
          doubleOptions={true}
          leftButtonText={DISCARD_REFUND_BUTTON}
          rightButtonText={UPGRADE_ACCOUNT_BUTTON}
          onLeftButtonClick={handleNavigateToDashboard}
          onRightButtonClick={handleUpgradeAccountTier}
        />
      </div>
    </>
  );
});

export default RefundPage;
